import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import "./ResetPassword.css";
import {
  EyeInvisibleOutlined,
  EyeTwoTone,
  KeyOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import { Input, Button, Typography, Modal } from "antd";
import { getToken } from "../utils/cryptoUtils";
import apiClient from "../utils/apiClient";

const { Title } = Typography;
const { confirm } = Modal;

const ResetPassword = () => {
  const [formData, setFormData] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  /* ================= INPUT CHANGE ================= */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError("");
    setSuccess("");
  };

  /* ================= CONFIRM MODAL ================= */
  const handleConfirmSubmit = () => {
    confirm({
      title: "Are you sure you want to change your password?",
      icon: <ExclamationCircleOutlined />,
      okText: "Yes",
      cancelText: "No",
      onOk: handleSubmit,
    });
  };

  /* ================= SUBMIT ================= */
  const handleSubmit = async () => {
    const { oldPassword, newPassword, confirmPassword } = formData;

    if (!oldPassword || !newPassword || !confirmPassword) {
      setError("All fields are required.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("New Password and Confirm Password do not match.");
      return;
    }

    if (newPassword.length < 6) {
      setError("Password must be at least 6 characters long.");
      return;
    }

    const token = getToken();
    if (!token) {
      setError("You are not logged in.");
      return;
    }

    try {
      setLoading(true);

      // ✅ apiClient RETURNS JSON DIRECTLY
      const data = await apiClient("/auth/change-password/", {
        method: "POST",
        body: JSON.stringify({
          old_password: oldPassword,
          new_password: newPassword,
        }),
      });

      setSuccess(data?.message || "Password changed successfully!");

      // Optional: auto logout / redirect
      setTimeout(() => {
        navigate("/");
      }, 2000);

    } catch (err) {
      console.error(err);
      setError(
        err?.message ||
        err?.error ||
        "Password change failed. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  /* ================= RESET ================= */
  const handleReset = () => {
    setFormData({
      oldPassword: "",
      newPassword: "",
      confirmPassword: "",
    });
    setError("");
    setSuccess("");
  };

  /* ================= UI ================= */
  return (
    <div className="reset-password-container">
      <Title level={3}>Change Password</Title>

      <form
        className="reset-password-form"
        onSubmit={(e) => e.preventDefault()}
      >
        <div className="form-group">
          <label>Old Password</label>
          <Input.Password
            name="oldPassword"
            value={formData.oldPassword}
            onChange={handleChange}
            prefix={<KeyOutlined />}
            iconRender={(v) =>
              v ? <EyeTwoTone /> : <EyeInvisibleOutlined />
            }
          />
        </div>

        <div className="form-group">
          <label>New Password</label>
          <Input.Password
            name="newPassword"
            value={formData.newPassword}
            onChange={handleChange}
            prefix={<KeyOutlined />}
            iconRender={(v) =>
              v ? <EyeTwoTone /> : <EyeInvisibleOutlined />
            }
          />
        </div>

        <div className="form-group">
          <label>Confirm Password</label>
          <Input.Password
            name="confirmPassword"
            value={formData.confirmPassword}
            onChange={handleChange}
            prefix={<KeyOutlined />}
            iconRender={(v) =>
              v ? <EyeTwoTone /> : <EyeInvisibleOutlined />
            }
          />
        </div>

        {error && <p className="error-text">{error}</p>}
        {success && <p className="success-text">{success}</p>}

        <div className="form-actions">
          <Button
            type="primary"
            onClick={handleConfirmSubmit}
            loading={loading}
          >
            SUBMIT
          </Button>

          <Button onClick={handleReset} className="reset-btn">
            RESET
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ResetPassword;
